// @flow

import React, { useState } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import moment from 'moment';
import styled from 'styled-components';
import relayEnvironment from 'shared/gql/relayEnvironment';
import { i18n, getCompanyCurrency, getCurrencySymbol } from 'shared/utils';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Loader from 'shared/components/common/Loader';
import { DateRangePicker } from 'shared/components/form';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { RouterHistory } from 'react-router';

// ---------- Styled Components ----------

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  flex-wrap: wrap;
`;

const FilterBtn = styled.button`
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid ${p => (p.active ? '#1890ff' : '#d9d9d9')};
  background: ${p => (p.active ? '#1890ff' : '#fff')};
  color: ${p => (p.active ? '#fff' : '#333')};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #1890ff;
    color: ${p => (p.active ? '#fff' : '#1890ff')};
  }
`;

const DatePickerWrapper = styled.div`
  margin-left: 8px;
  min-width: 260px;
`;

const FilterLabel = styled.span`
  font-size: 13px;
  color: #666;
  font-weight: 500;
  margin-right: 4px;
`;

const Content = styled.div`
  padding: 24px;
`;

const KPIRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
`;

const KPICard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
`;

const KPILabel = styled.span`
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const KPIValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #333;
`;

const KPISubtext = styled.span`
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  padding: 20px;
`;

const ChartCardFull = styled(ChartCard)`
  grid-column: 1 / -1;
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0 0 16px 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const SimpleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const STh = styled.th`
  text-align: ${p => p.align || 'left'};
  padding: 8px 12px;
  border-bottom: 2px solid #e8e8e8;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
`;

const STd = styled.td`
  text-align: ${p => p.align || 'left'};
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
`;

// ---------- Filter Presets ----------

const FILTER_PRESETS = [
  {
    key: 'this-month',
    label: 'This Month',
    getRange: () => ({
      start: moment().startOf('month'),
      end: moment().endOf('day'),
    }),
  },
  {
    key: 'last-month',
    label: 'Last Month',
    getRange: () => ({
      start: moment().subtract(1, 'month').startOf('month'),
      end: moment().subtract(1, 'month').endOf('month'),
    }),
  },
  {
    key: 'this-quarter',
    label: 'This Quarter',
    getRange: () => ({
      start: moment().startOf('quarter'),
      end: moment().endOf('day'),
    }),
  },
  {
    key: 'ytd',
    label: 'Year to Date',
    getRange: () => ({
      start: moment().startOf('year'),
      end: moment().endOf('day'),
    }),
  },
  {
    key: 'last-12',
    label: 'Last 12 Months',
    getRange: () => ({
      start: moment().subtract(12, 'months').startOf('month'),
      end: moment().endOf('day'),
    }),
  },
  {
    key: 'last-year',
    label: 'Last Year',
    getRange: () => ({
      start: moment().subtract(1, 'year').startOf('year'),
      end: moment().subtract(1, 'year').endOf('year'),
    }),
  },
  {
    key: 'all-time',
    label: 'All Time',
    getRange: () => ({
      start: null,
      end: null,
    }),
  },
  {
    key: 'custom',
    label: 'Custom',
    getRange: () => ({
      start: moment().subtract(30, 'days').startOf('day'),
      end: moment().endOf('day'),
    }),
  },
];

const filterByDateRange = (
  items: Array<Object>,
  start: ?moment$Moment,
  end: ?moment$Moment,
  dateField: string = 'createdAt',
  altDateField?: string,
) => {
  if (!start && !end) return items;
  return items.filter(item => {
    const dateStr = item[altDateField] || item[dateField];
    if (!dateStr) return false;
    const date = moment(dateStr);
    if (start && date.isBefore(start)) return false;
    if (end && date.isAfter(end)) return false;
    return true;
  });
};

// ---------- Constants ----------

const COLORS = [
  '#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1',
  '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb',
];

const PIE_COLORS = {
  OPEN: '#1890ff',
  CLOSED: '#52c41a',
  PENDING_APPROVAL: '#faad14',
  APPROVED: '#1890ff',
  SENT: '#13c2c2',
  PARTIALLY_RECEIVED: '#722ed1',
  RECEIVED: '#52c41a',
  REJECTED: '#f5222d',
  CANCELLED: '#999',
  DRAFT: '#d9d9d9',
  PARTIALLY_PAID: '#faad14',
  PAID: '#52c41a',
  OVERDUE: '#f5222d',
  VOID: '#999',
};

const formatCurrency = (value: ?number) => {
  if (value == null) return `${getCurrencySymbol()}0.00`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: getCompanyCurrency(),
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatLabel = (str: string) =>
  (str || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ---------- Data Processing ----------

const buildMonthlySalesData = (
  orders: Array<Object>,
  rangeStart: ?moment$Moment,
  rangeEnd: ?moment$Moment,
) => {
  const months = {};
  const end = rangeEnd || moment();
  const start = rangeStart || moment().subtract(12, 'months').startOf('month');
  const totalMonths = Math.min(end.diff(start, 'months') + 1, 60);

  for (let i = totalMonths - 1; i >= 0; i--) {
    const key = end.clone().subtract(i, 'months').format('MMM YYYY');
    months[key] = { month: key, revenue: 0, count: 0 };
  }

  orders.forEach(order => {
    const date = order.orderedAt || order.createdAt;
    if (!date) return;
    const key = moment(date).format('MMM YYYY');
    if (months[key]) {
      months[key].revenue += order.totalAmount || 0;
      months[key].count += 1;
    }
  });

  return Object.values(months);
};

const buildCustomerRevenueData = (orders: Array<Object>) => {
  const customerMap = {};

  orders.forEach(order => {
    const name = order.customer?.name || 'Unknown';
    if (!customerMap[name]) {
      customerMap[name] = { customer: name, revenue: 0, orders: 0 };
    }
    customerMap[name].revenue += order.totalAmount || 0;
    customerMap[name].orders += 1;
  });

  return Object.values(customerMap)
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
};

const buildStatusBreakdown = (items: Array<Object>, statusField: string = 'status') => {
  const statusMap = {};

  items.forEach(item => {
    const status = item[statusField] || 'UNKNOWN';
    if (!statusMap[status]) {
      statusMap[status] = { name: formatLabel(status), value: 0, status };
    }
    statusMap[status].value += 1;
  });

  return Object.values(statusMap);
};

const buildMonthlyPOSpend = (
  purchaseOrders: Array<Object>,
  rangeStart: ?moment$Moment,
  rangeEnd: ?moment$Moment,
) => {
  const months = {};
  const end = rangeEnd || moment();
  const start = rangeStart || moment().subtract(12, 'months').startOf('month');
  const totalMonths = Math.min(end.diff(start, 'months') + 1, 60);

  for (let i = totalMonths - 1; i >= 0; i--) {
    const key = end.clone().subtract(i, 'months').format('MMM YYYY');
    months[key] = { month: key, spend: 0, count: 0 };
  }

  purchaseOrders.forEach(po => {
    const date = po.createdAt;
    if (!date) return;
    const key = moment(date).format('MMM YYYY');
    if (months[key]) {
      months[key].spend += po.totalAmount || 0;
      months[key].count += 1;
    }
  });

  return Object.values(months);
};

const buildTopItemsSold = (orders: Array<Object>) => {
  const itemMap = {};

  orders.forEach(order => {
    if (!order.yields || !order.yields.edges) return;
    order.yields.edges.forEach(edge => {
      const item = edge.node.item;
      if (!item) return;
      const key = item.id;
      if (!itemMap[key]) {
        itemMap[key] = {
          name: item.name,
          itemNumber: item.itemNumber || '',
          totalQty: 0,
          orderCount: 0,
        };
      }
      itemMap[key].totalQty += edge.node.orderedQuantity || 0;
      itemMap[key].orderCount += 1;
    });
  });

  return Object.values(itemMap)
    .sort((a, b) => b.totalQty - a.totalQty)
    .slice(0, 10);
};

// ---------- Custom Tooltip ----------

const CustomTooltip = ({ active, payload, label }: Object) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ color: entry.color }}>
          {entry.name}: {typeof entry.value === 'number' && entry.name !== 'Orders' && entry.name !== 'POs'
            ? formatCurrency(entry.value)
            : entry.value}
        </div>
      ))}
    </div>
  );
};

// ---------- Main Component ----------

type Props = {
  history: RouterHistory,
};

const SalesAnalytics = ({ history }: Props) => {
  const [activePreset, setActivePreset] = useState('last-12');
  const [customRange, setCustomRange] = useState({
    startDate: moment().subtract(30, 'days').startOf('day'),
    endDate: moment().endOf('day'),
  });

  const preset = FILTER_PRESETS.find(p => p.key === activePreset);
  const range = preset ? preset.getRange() : { start: null, end: null };
  const filterStart = activePreset === 'custom' ? customRange.startDate : range.start;
  const filterEnd = activePreset === 'custom' ? customRange.endDate : range.end;

  const rangeLabel = filterStart && filterEnd
    ? `${filterStart.format('MMM D, YYYY')} — ${filterEnd.format('MMM D, YYYY')}`
    : i18n.t('All Time');

  return (
    <Page>
      <PageHeader title={i18n.t('Sales Analytics')} />
      <FilterBar>
        <FilterLabel>{i18n.t('Period:')}</FilterLabel>
        {FILTER_PRESETS.map(p => (
          <FilterBtn
            key={p.key}
            active={activePreset === p.key}
            onClick={() => setActivePreset(p.key)}
          >
            {i18n.t(p.label)}
          </FilterBtn>
        ))}
        {activePreset === 'custom' && (
          <DatePickerWrapper>
            <DateRangePicker
              value={{
                startDate: customRange.startDate,
                endDate: customRange.endDate,
              }}
              onChange={({ startDate, endDate }) => {
                setCustomRange({
                  startDate: startDate ? moment(startDate).startOf('day') : customRange.startDate,
                  endDate: endDate ? moment(endDate).endOf('day') : customRange.endDate,
                });
              }}
            />
          </DatePickerWrapper>
        )}
      </FilterBar>
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query SalesAnalyticsQuery {
            orders(first: 5000) {
              edges {
                node {
                  id
                  orderNumber
                  status
                  totalAmount
                  subtotal
                  discountAmount
                  taxAmount
                  orderedAt
                  createdAt
                  customer {
                    id
                    name
                  }
                  yields(first: 100) {
                    edges {
                      node {
                        id
                        orderedQuantity
                        item {
                          id
                          name
                          itemNumber
                        }
                      }
                    }
                  }
                }
              }
            }
            purchaseOrders(first: 5000) {
              edges {
                node {
                  id
                  poNumber
                  status
                  totalAmount
                  vendor {
                    id
                    name
                  }
                  createdAt
                }
              }
            }
            invoices(first: 5000) {
              edges {
                node {
                  id
                  invoiceNumber
                  status
                  totalAmount
                  amountPaid
                  amountDue
                  customer {
                    id
                    name
                  }
                  issueDate
                  dueDate
                  createdAt
                }
              }
            }
          }
        `}
        render={({ props }) => {
          if (!props) return <Content><Loader /></Content>;

          const allOrders = (props.orders?.edges || []).map(e => e.node);
          const allPurchaseOrders = (props.purchaseOrders?.edges || []).map(e => e.node);
          const allInvoices = (props.invoices?.edges || []).map(e => e.node);

          // Apply date filter
          const orders = filterByDateRange(allOrders, filterStart, filterEnd, 'createdAt', 'orderedAt');
          const purchaseOrders = filterByDateRange(allPurchaseOrders, filterStart, filterEnd, 'createdAt');
          const invoices = filterByDateRange(allInvoices, filterStart, filterEnd, 'createdAt', 'issueDate');

          // KPIs
          const totalSalesRevenue = orders.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
          const activeOrders = orders.filter(o => o.status !== 'DELIVERED').length;
          const deliveredOrders = orders.filter(o => o.status === 'DELIVERED').length;
          const avgOrderValue = orders.length > 0 ? totalSalesRevenue / orders.length : 0;

          const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + (po.totalAmount || 0), 0);
          const activePOs = purchaseOrders.filter(po =>
            !['RECEIVED', 'CANCELLED', 'REJECTED'].includes(po.status)
          ).length;

          const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
          const totalCollected = invoices.reduce((sum, inv) => sum + (inv.amountPaid || 0), 0);
          const totalOutstanding = invoices.reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
          const overdueInvoices = invoices.filter(inv => inv.status === 'OVERDUE').length;

          // Chart data
          const monthlySales = buildMonthlySalesData(orders, filterStart, filterEnd);
          const customerRevenue = buildCustomerRevenueData(orders);
          const orderStatusData = buildStatusBreakdown(orders);
          const poStatusData = buildStatusBreakdown(purchaseOrders);
          const invoiceStatusData = buildStatusBreakdown(invoices);
          const monthlyPOSpend = buildMonthlyPOSpend(purchaseOrders, filterStart, filterEnd);
          const topItems = buildTopItemsSold(orders);

          return (
            <Content>
              {/* Active Range Label */}
              <div style={{ marginBottom: 16, fontSize: 13, color: '#999' }}>
                {i18n.t('Showing data for')}: <strong style={{ color: '#333' }}>{rangeLabel}</strong>
              </div>

              {/* Sales KPIs */}
              <KPIRow>
                <KPICard>
                  <KPILabel>{i18n.t('Total Sales Revenue')}</KPILabel>
                  <KPIValue>{formatCurrency(totalSalesRevenue)}</KPIValue>
                  <KPISubtext>{orders.length} {i18n.t('orders')}</KPISubtext>
                </KPICard>
                <KPICard>
                  <KPILabel>{i18n.t('Avg. Order Value')}</KPILabel>
                  <KPIValue>{formatCurrency(avgOrderValue)}</KPIValue>
                  <KPISubtext>{activeOrders} {i18n.t('active')} / {deliveredOrders} {i18n.t('delivered')}</KPISubtext>
                </KPICard>
                <KPICard>
                  <KPILabel>{i18n.t('Total PO Spend')}</KPILabel>
                  <KPIValue>{formatCurrency(totalPOSpend)}</KPIValue>
                  <KPISubtext>{activePOs} {i18n.t('active POs')}</KPISubtext>
                </KPICard>
                <KPICard>
                  <KPILabel>{i18n.t('Total Invoiced')}</KPILabel>
                  <KPIValue>{formatCurrency(totalInvoiced)}</KPIValue>
                  <KPISubtext>{formatCurrency(totalCollected)} {i18n.t('collected')}</KPISubtext>
                </KPICard>
                <KPICard>
                  <KPILabel>{i18n.t('Outstanding')}</KPILabel>
                  <KPIValue style={{ color: totalOutstanding > 0 ? '#f5222d' : '#52c41a' }}>
                    {formatCurrency(totalOutstanding)}
                  </KPIValue>
                  <KPISubtext>{overdueInvoices} {i18n.t('overdue invoices')}</KPISubtext>
                </KPICard>
              </KPIRow>

              {/* Sales Revenue Over Time */}
              <ChartCardFull>
                <ChartTitle>{i18n.t('Sales Revenue')}</ChartTitle>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlySales}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="revenue" name={i18n.t('Revenue')} fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCardFull>

              <ChartGrid>
                {/* Top 10 Customers by Revenue */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Top 10 Customers by Revenue')}</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={customerRevenue} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="customer" type="category" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="revenue" name={i18n.t('Revenue')} fill="#52c41a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Order Status Breakdown */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Order Status Breakdown')}</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name} (${value})`}
                      >
                        {orderStatusData.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* PO Spend Over Time */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Purchase Order Spend')}</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyPOSpend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={v => formatCurrency(v)} tick={{ fontSize: 11 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="spend" name={i18n.t('Spend')} stroke="#722ed1" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* PO Status Breakdown */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Purchase Order Status')}</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={poStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name} (${value})`}
                      >
                        {poStatusData.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Invoice Status */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Invoice Status')}</ChartTitle>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={invoiceStatusData}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value }) => `${name} (${value})`}
                      >
                        {invoiceStatusData.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[entry.status] || COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>

                {/* Top Items Sold */}
                <ChartCard>
                  <ChartTitle>{i18n.t('Top 10 Items Sold')}</ChartTitle>
                  <TableWrapper>
                    <SimpleTable>
                      <thead>
                        <tr>
                          <STh>#</STh>
                          <STh>{i18n.t('Item')}</STh>
                          <STh>{i18n.t('Part #')}</STh>
                          <STh align="right">{i18n.t('Total Qty')}</STh>
                          <STh align="right">{i18n.t('Orders')}</STh>
                        </tr>
                      </thead>
                      <tbody>
                        {topItems.map((item, i) => (
                          <tr key={i}>
                            <STd>{i + 1}</STd>
                            <STd>{item.name}</STd>
                            <STd>{item.itemNumber || '—'}</STd>
                            <STd align="right">{item.totalQty.toLocaleString()}</STd>
                            <STd align="right">{item.orderCount}</STd>
                          </tr>
                        ))}
                        {topItems.length === 0 && (
                          <tr>
                            <STd colSpan={5} align="center" style={{ color: '#999' }}>
                              {i18n.t('No data available')}
                            </STd>
                          </tr>
                        )}
                      </tbody>
                    </SimpleTable>
                  </TableWrapper>
                </ChartCard>
              </ChartGrid>
            </Content>
          );
        }}
      />
    </Page>
  );
};

export default SalesAnalytics;
